import type { User } from '@supabase/supabase-js'
import { detectBrowserCountryCode, getCountryLocale } from './groceryPrices'
import {
  readStoredLanguagePreference,
  resolveAnonymousLanguage,
} from './storedLanguage'
import type { AppLanguage } from '../i18n/languages'
import { clientTimezone } from './clientContext'
import { supabase } from './supabaseClient'

export type MedicationsInfo = {
  takes_medications: boolean
  details?: string
}

export type ProfileRow = {
  id: string
  email: string
  onboarding_step: number
  onboarding_completed: boolean
  [key: string]: unknown
}

export function medicationsToFormState(medications: unknown): { flag: string; details: string } {
  if (medications && typeof medications === 'object' && 'takes_medications' in medications) {
    const info = medications as MedicationsInfo
    return {
      flag: info.takes_medications ? 'true' : 'false',
      details: info.details ?? '',
    }
  }
  return { flag: 'false', details: '' }
}

export function formStateToMedications(takesMedications: string, details: string): MedicationsInfo {
  const takes = takesMedications === 'true'
  const info: MedicationsInfo = { takes_medications: takes }
  if (takes && details.trim()) {
    info.details = details.trim()
  }
  return info
}

/** Ensures a profiles row exists for the auth user (client-side fallback if DB trigger is missing). */
export async function ensureProfile(
  user: Pick<User, 'id' | 'email' | 'user_metadata'>,
  options?: { languagePreference?: AppLanguage },
): Promise<{
  profile: ProfileRow | null
  error: string | null
  created: boolean
}> {
  const languagePreference = options?.languagePreference
    ?? readStoredLanguagePreference()
    ?? resolveAnonymousLanguage()

  const { data: existing, error: fetchError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle()

  if (fetchError) {
    return { profile: null, error: fetchError.message, created: false }
  }

  if (existing) {
    if (!existing.language_preference) {
      const { data: updated, error: updateError } = await supabase
        .from('profiles')
        .update({ language_preference: languagePreference })
        .eq('id', user.id)
        .select()
        .single()

      if (!updateError && updated) {
        return { profile: updated as ProfileRow, error: null, created: false }
      }
    }

    return { profile: existing as ProfileRow, error: null, created: false }
  }

  const browserCountry = detectBrowserCountryCode()
  const locale = getCountryLocale(browserCountry)

  const { data: inserted, error: insertError } = await supabase
    .from('profiles')
    .insert({
      id: user.id,
      email: user.email ?? '',
      onboarding_step: 0,
      onboarding_completed: false,
      calibration_status: 'not_started',
      calibration_completed_sessions: 0,
      country_code: browserCountry,
      currency_code: locale.currency_code,
      currency_symbol: locale.currency_symbol,
      preferred_currency: locale.currency_code,
      timezone: clientTimezone(),
      language_preference: languagePreference,
    })
    .select()
    .single()

  if (insertError) {
    return { profile: null, error: insertError.message, created: false }
  }

  return { profile: inserted as ProfileRow, error: null, created: true }
}
